/**
 * Frontend API client
 * Based on $fetch (Nuxt built-in), handles auth, errors, and request interceptors
 */

import type { FetchOptions } from 'ofetch'
import { readApiErrorKey } from '~/utils/apiError'
import { mergeRequestHeaders } from '~/utils/requestHeaders'

const API_TIMEOUT = 30000
const SUCCESS_CODE = 200

/** Strip HTML tags from error messages to prevent XSS in toast notifications */
function stripHtml(text: string): string {
  if (!text) return ''
  return text.replace(/<[^>]*>/g, '')
}

function getBaseURL(): string {
  if (import.meta.server) {
    try {
      const config = useRuntimeConfig()
      if (config.apiBaseUrlServer) return config.apiBaseUrlServer as string
    }
    catch {
      // Nuxt context may be lost in $fetch interceptors during SSR
    }
    return process.env.NUXT_API_BASE_URL_SERVER || ''
  }
  try {
    const config = useRuntimeConfig()
    return config.public.apiBaseUrl as string || ''
  }
  catch {
    return ''
  }
}

/**
 * Unified API response format (matches backend RestResult<T>)
 */
export interface ApiResponse<T = any> {
  code: number
  message: string
  data: T
}

export class ApiError extends Error {
  status?: number
  code?: number | string
  data?: unknown
  errorKey?: string

  constructor(message: string, options: { status?: number, code?: number | string, data?: unknown, errorKey?: string } = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = options.status
    this.code = options.code
    this.data = options.data
    this.errorKey = options.errorKey
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

function responseError(response: { status?: number, statusText?: string, _data?: any, headers?: Headers }): ApiError {
  const data = response._data
  let message = data?.message || response.statusText || 'Request failed'
  if (data?.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
    const fieldErrors = Object.values(data.data).filter(Boolean) as string[]
    if (fieldErrors.length > 0) message = fieldErrors.join('; ')
  }
  return new ApiError(stripHtml(message), {
    status: response.status,
    code: data?.code,
    data: data?.data,
    errorKey: readApiErrorKey(response.headers),
  })
}

/**
 * Backend page result format
 */
export interface BackendPageResult<T> {
  data: T[]
  total: number
  page: number
  size: number
  currentPage: number
  offset: number
  totalPage: number
}

/**
 * Frontend page result format
 */
export interface PageResult<T> {
  items: T[]
  total: number
  page: number
  size: number
  totalPages: number
}

/**
 * API client instance
 */
export const apiClient = $fetch.create({
  timeout: API_TIMEOUT,
  credentials: 'include',

  onRequest({ options }) {
    options.baseURL = getBaseURL()
    options.credentials = 'include'

    // Resolve locale from cookie (SSR-safe). Default to the site's primary
    // locale (es), not en — an unset cookie means "no explicit choice yet",
    // and the storefront default is Spanish.
    let locale = 'es'
    try {
      const localeCookie = useCookie('i18n_redirected')
      if (localeCookie.value) locale = localeCookie.value
    }
    catch {
      // Nuxt context may be lost
    }

    const headers = mergeRequestHeaders(options.headers, {
      'Content-Type': 'application/json',
      'X-Locale': locale,
      'X-Timezone': import.meta.client ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC',
    })
    options.headers = headers

    // Auth on SSR: server-to-server fetch ignores credentials:'include',
    // so we must explicitly forward the browser's original Cookie header.
    // (HttpOnly is irrelevant server-side — request.headers.cookie has it.)
    if (import.meta.server) {
      try {
        const reqHeaders = useRequestHeaders(['cookie'])
        if (reqHeaders.cookie) {
          headers.set('Cookie', reqHeaders.cookie)
        }
      }
      catch {
        // Nuxt context may be lost in deep async chains; fall through.
      }
    }

    // Best-effort Authorization header (covers cases where useCookie is
    // reachable but useRequestHeaders is not, and any future client-side
    // token surfaced via useCookie).
    try {
      const tokenCookie = useCookie('_authentication')
      const token = tokenCookie.value
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }
    }
    catch {
      // Nuxt context may be lost in $fetch interceptors during SSR.
    }
  },

  async onResponse({ response }) {
    const data = response._data

    if (response.status === 401) {
      throw new ApiError('Authentication required', {
        status: 401,
        code: data?.code,
        errorKey: readApiErrorKey(response.headers),
      })
    }

    // Token refresh is delivered exclusively via the backend Set-Cookie header
    // (HttpOnly + Domain-scoped). Do NOT mirror it into a client-side cookie —
    // doing so would create a second `_authentication` cookie with different
    // attributes and clobber the canonical one.

    if (data?.code !== undefined && data.code !== SUCCESS_CODE) {
      throw responseError(response)
    }

    return response
  },

  async onResponseError({ response }) {
    if (response?.status === 401) {
      throw new ApiError('Authentication required', {
        status: 401,
        code: response?._data?.code,
        errorKey: readApiErrorKey(response?.headers),
      })
    }
    if (response) throw responseError(response)
    throw new ApiError('Request failed')
  },
})

function isPageResult(data: any): data is BackendPageResult<any> {
  return (
    data &&
    typeof data === 'object' &&
    Array.isArray(data.data) &&
    typeof data.total === 'number' &&
    typeof data.page === 'number' &&
    typeof data.size === 'number' &&
    typeof data.totalPage === 'number'
  )
}

function transformPageResult<T>(backendResult: BackendPageResult<T>): PageResult<T> {
  return {
    items: backendResult.data,
    total: backendResult.total,
    page: backendResult.page,
    size: backendResult.size,
    totalPages: backendResult.totalPage,
  }
}

interface RequestOptions extends Omit<FetchOptions, 'method'> {}

export async function apiGet<T = any>(url: string, options?: RequestOptions): Promise<T> {
  const response = await apiClient<ApiResponse<T>>(url, {
    ...options,
    method: 'GET',
  })

  if (isPageResult(response.data)) {
    return transformPageResult(response.data) as T
  }

  return response.data
}

export async function apiPost<T = any>(url: string, body?: any, options?: RequestOptions): Promise<T> {
  const response = await apiClient<ApiResponse<T>>(url, {
    ...options,
    method: 'POST',
    body,
  })
  return response.data
}

export async function apiPut<T = any>(url: string, body?: any, options?: RequestOptions): Promise<T> {
  const response = await apiClient<ApiResponse<T>>(url, {
    ...options,
    method: 'PUT',
    body,
  })
  return response.data
}

export async function apiDelete<T = any>(url: string, options?: RequestOptions): Promise<T> {
  const response = await apiClient<ApiResponse<T>>(url, {
    ...options,
    method: 'DELETE',
  })
  return response.data
}

export interface UploadImageResponse {
  path: string
  url: string
  originalFilename: string
  contentType: string
  size: number
}

export async function apiUploadImage(file: File, category: string): Promise<UploadImageResponse> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('category', category)

  const baseURL = getBaseURL()
  const tokenCookie = useCookie('_authentication')
  const headers: Record<string, string> = {}
  if (tokenCookie.value) {
    headers.Authorization = `Bearer ${tokenCookie.value}`
  }

  const response = await $fetch<ApiResponse<UploadImageResponse>>(`${baseURL}/api/app/v1/upload/image`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
    headers,
    timeout: API_TIMEOUT,
  })

  if (response.code !== SUCCESS_CODE) {
    throw new Error(response.message || 'Upload failed')
  }

  return response.data
}

export function buildQueryParams(params: Record<string, any>): URLSearchParams {
  const searchParams = new URLSearchParams()

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value))
    }
  })

  return searchParams
}
